import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import { metaLead } from "@/lib/pixel";
import { ArrowUpRight, ArrowRight, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { Kicker } from "@/components/Section";
import Marquee from "@/components/Marquee";
import TiltCard from "@/components/TiltCard";
import Reveal from "@/components/Reveal";
import ParallaxImage from "@/components/ParallaxImage";
import WeatherWidget from "@/components/WeatherWidget";
import WeekCoursesBanner from "@/components/WeekCoursesBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMG = "https://images.pexels.com/photos/6573881/pexels-photo-6573881.jpeg";
const DEFAULT_NEWS_IMG = "https://images.unsplash.com/photo-1763917379121-91130139aca0?crop=entropy&cs=srgb&fm=jpg&q=85&w=600";

// Abgeschrägte Ecken (achteckiger Look) für News-Kacheln
const BEVEL = 14;
const clip = {
  clipPath: `polygon(${BEVEL}px 0, calc(100% - ${BEVEL}px) 0, 100% ${BEVEL}px, 100% calc(100% - ${BEVEL}px), calc(100% - ${BEVEL}px) 100%, ${BEVEL}px 100%, 0 calc(100% - ${BEVEL}px), 0 ${BEVEL}px)`,
};

const Home = () => {
  const { t, lang } = useLang();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [news, setNews] = useState([]);
  const [activeNews, setActiveNews] = useState(null);
  const videoRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/news`).then((r) => setNews(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!activeNews) return;
    const onKey = (e) => { if (e.key === "Escape") setActiveNews(null); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [activeNews]);

  const fmtDate = (d) =>
    new Date(d).toLocaleDateString(lang === "de" ? "de-DE" : "en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    // React sets `muted` unreliably via JSX prop → set on the DOM node so autoplay is allowed.
    v.muted = true;
    v.defaultMuted = true;
    v.playsInline = true;
    const tryPlay = () => {
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    };
    tryPlay();
    v.addEventListener("canplay", tryPlay);
    // resume if the tab/app becomes visible again
    const onVis = () => { if (document.visibilityState === "visible") tryPlay(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      v.removeEventListener("canplay", tryPlay);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { name, email, lang });
      metaLead({ contentName: "Newsletter Signup", source: "home", email });
      toast.success(t.home.subscribed);
      setName("");
      setEmail("");
    } catch (err) {
      toast.error("Error / Fehler");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="home-page">
      {/* KURSE DIESE WOCHE – Banner direkt unter dem Header */}
      <div className="pt-20">
        <WeekCoursesBanner lang={lang} />
      </div>

      {/* VIDEO INTRO */}
      <section className="relative w-full bg-[#050505]" data-testid="hero-video-section">
        <div className="relative w-full overflow-hidden">
          <video
            ref={videoRef}
            data-testid="hero-video"
            className="w-full h-auto object-cover block"
            src="/hero-intro-web.mp4"
            poster="/hero-poster.jpg"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          />
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-[#050505] pointer-events-none" />
        </div>
      </section>

      {/* PROGRAMME / OFFERS */}
      <section className="pt-10 pb-20 md:pt-14 md:pb-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="offers-section">
        <Kicker>{t.home.offersKicker}</Kicker>
        <h2 className="pulse-hover font-heading text-4xl md:text-6xl uppercase tracking-tight text-white mt-3 mb-12 block w-fit origin-left cursor-default">
          {t.home.offersTitle}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
          {t.home.offers.map((o, i) => {
            const isInfo = i === 2;
            const btnLabel = isInfo
              ? (lang === "de" ? "Zu den Infos" : "View info")
              : (lang === "de" ? "Termin buchen" : "Book now");
            return (
              <Reveal key={o.to} direction="up" delay={i * 0.07} className="h-full">
              <TiltCard
                max={5}
                data-testid={`offer-card-${i}`}
                className="bg-[#0F0F0F] border border-[#ff0033]/60 p-6 sm:p-8 md:p-10 hover:border-[#ff0033] transition-colors group flex flex-col h-full sm:min-h-[320px] md:min-h-[380px]"
              >
                <span className="font-mono-lab text-xs text-neutral-600">0{i + 1}</span>
                <h3 className="font-heading text-2xl sm:text-3xl md:text-4xl uppercase tracking-tight text-white mt-4 group-hover:text-[#ff0033] transition-colors">
                  {o.t}
                </h3>
                <p className="text-neutral-300 text-sm sm:text-base md:text-lg mt-4 leading-relaxed flex-1">{o.d}</p>
                <div className="flex justify-end mt-6">
                  <Link
                    to={o.to}
                    data-testid={`offer-cta-${i}`}
                    className="inline-flex items-center gap-2 bg-[#ff0033] text-white font-heading uppercase tracking-wider px-4 sm:px-6 py-3 text-xs sm:text-sm md:text-base hover:bg-[#cc0029] transition-colors"
                  >
                    {btnLabel} <ArrowUpRight size={16} />
                  </Link>
                </div>
              </TiltCard>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* NEWSLETTER + WETTER – zwei quadratische Kacheln nebeneinander (50/50) */}
      <section className="pb-20 md:pb-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="newsletter-section">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 items-stretch">
          {/* Newsletter-Kachel */}
          <Reveal direction="up" className="h-full">
            <div className="relative overflow-hidden bg-[#0F0F0F] border border-white/10 p-6 sm:p-8 flex flex-col h-full sm:min-h-[320px] md:min-h-[380px]">
              <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(255,0,51,0.35), rgba(255,0,51,0) 70%)", filter: "blur(60px)" }} />
              <div className="relative z-10 flex flex-col h-full">
                <Kicker>// NEWSLETTER</Kicker>
                <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-tight text-white mt-3 leading-none">
                  {t.home.newsletterLabel}
                </h2>
                <p className="text-neutral-400 text-sm md:text-base mt-4 leading-relaxed">
                  {lang === "de"
                    ? "Neue Kurse, Events, Angebote & Neuigkeiten der Golfcoaching Academy Wendlingen – direkt in dein Postfach. Jederzeit abbestellbar."
                    : "New courses, events, offers & news from Golfcoaching Academy Wendlingen – straight to your inbox. Unsubscribe anytime."}
                </p>
                <form onSubmit={subscribe} className="mt-auto pt-6 flex flex-col gap-3" data-testid="newsletter-form">
                  <input data-testid="newsletter-name-input" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={t.home.namePh}
                    className="w-full bg-[#1A1A1A] border border-white/10 text-white p-3.5 font-body focus:border-[#ff0033] outline-none transition-colors placeholder:text-neutral-600" />
                  <input data-testid="newsletter-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.home.emailPh}
                    className="w-full bg-[#1A1A1A] border border-white/10 text-white p-3.5 font-body focus:border-[#ff0033] outline-none transition-colors placeholder:text-neutral-600" />
                  <button data-testid="newsletter-submit-btn" type="submit" disabled={loading}
                    className="w-full bg-[#ff0033] text-white font-heading uppercase tracking-wider py-3.5 text-lg hover:bg-[#cc0029] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                    {loading ? <Loader2 className="animate-spin" size={20} /> : t.home.subscribe}
                  </button>
                </form>
              </div>
            </div>
          </Reveal>

          {/* Wetter-Kachel */}
          <Reveal direction="up" delay={0.07} className="h-full">
            <WeatherWidget />
          </Reveal>
        </div>
      </section>

      <Marquee />

      {/* NEWS – von der früheren News-Seite auf die Home-Seite fusioniert */}
      {news.length > 0 && (
        <section className="pt-16 pb-20 md:pt-20 md:pb-28 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="home-news-section">
          <Kicker>{t.home.newsKicker}</Kicker>
          <h2 className="font-heading text-4xl md:text-6xl uppercase tracking-tight text-white mt-3 mb-10 md:mb-12">
            {t.home.newsTitle}
          </h2>
          <div className="space-y-5 md:space-y-6">
            {news.map((item, idx) => {
              const body = lang === "de" ? item.body_de : item.body_en;
              return (
                <Reveal as="article" key={item.id} direction="up" delay={Math.min(idx, 4) * 0.06}
                  data-testid={`news-card-${item.id}`}
                  onClick={() => setActiveNews(item)}
                  role="button" tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setActiveNews(item); } }}
                  className="group relative bg-white/10 hover:bg-[#ff0033]/50 transition-colors p-[1.5px] cursor-pointer"
                  style={clip}
                >
                  <div className="relative bg-[#0F0F0F] flex flex-col sm:flex-row overflow-hidden" style={clip}>
                    <div className="relative w-full sm:w-44 md:w-52 shrink-0 aspect-[16/9] sm:aspect-auto sm:min-h-[150px] overflow-hidden bg-black">
                      <img
                        src={item.image || DEFAULT_NEWS_IMG}
                        alt={lang === "de" ? item.title_de : item.title_en}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        data-testid={`news-image-${item.id}`}
                      />
                    </div>
                    <div className="p-6 md:p-7 flex flex-col justify-center flex-1">
                      <div className="flex items-center gap-4 mb-4">
                        <span className="font-mono-lab text-[10px] tracking-[0.2em] text-[#ff0033] uppercase border border-[#ff0033]/40 px-2 py-1">
                          {lang === "de" ? item.tag_de : item.tag_en}
                        </span>
                        <span className="font-mono-lab text-[10px] text-neutral-500">{fmtDate(item.date)}</span>
                      </div>
                      <h3 className="font-heading text-2xl md:text-3xl uppercase tracking-tight text-white leading-[1.08] mb-3 group-hover:text-[#ff0033] transition-colors">
                        {lang === "de" ? item.title_de : item.title_en}
                      </h3>
                      <p className="text-neutral-400 text-sm md:text-base leading-relaxed line-clamp-2" data-testid={`news-body-${item.id}`}>
                        {body}
                      </p>
                      <span
                        className="mt-3 self-start inline-flex items-center gap-1.5 font-mono-lab text-[10px] uppercase tracking-widest text-[#ff0033] group-hover:text-white transition-colors"
                        data-testid={`news-readmore-${item.id}`}
                      >
                        {lang === "de" ? "Weiterlesen" : "Read more"} <ArrowRight size={14} />
                      </span>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </section>
      )}

      {/* NEWS-POPUP: Bild voll sichtbar + ganzer Text (per Portal, damit es über Navbar liegt) */}
      {activeNews && createPortal(
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6 bg-black/85 backdrop-blur-sm animate-in fade-in duration-200"
          data-testid="news-modal-overlay"
          onClick={() => setActiveNews(null)}
        >
          <div
            className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0F0F0F] border border-white/10"
            style={clip}
            onClick={(e) => e.stopPropagation()}
            data-testid="news-modal"
          >
            <button
              onClick={() => setActiveNews(null)}
              data-testid="news-modal-close"
              aria-label="Schließen"
              className="absolute top-3 right-3 z-10 w-10 h-10 flex items-center justify-center bg-black/60 hover:bg-[#ff0033] text-white transition-colors rounded-full"
            >
              <X size={18} />
            </button>
            <div className="w-full bg-black flex items-center justify-center">
              <img
                src={activeNews.image || DEFAULT_NEWS_IMG}
                alt={lang === "de" ? activeNews.title_de : activeNews.title_en}
                className="w-full max-h-[55vh] object-contain"
                data-testid="news-modal-image"
              />
            </div>
            <div className="p-6 md:p-8">
              <div className="flex items-center gap-4 mb-4">
                <span className="font-mono-lab text-[10px] tracking-[0.2em] text-[#ff0033] uppercase border border-[#ff0033]/40 px-2 py-1">
                  {lang === "de" ? activeNews.tag_de : activeNews.tag_en}
                </span>
                <span className="font-mono-lab text-[10px] text-neutral-500">{fmtDate(activeNews.date)}</span>
              </div>
              <h3 className="font-heading text-3xl md:text-4xl uppercase tracking-tight text-white leading-[1.08] mb-4" data-testid="news-modal-title">
                {lang === "de" ? activeNews.title_de : activeNews.title_en}
              </h3>
              <p className="text-neutral-300 text-sm md:text-base leading-relaxed whitespace-pre-line" data-testid="news-modal-body">
                {lang === "de" ? activeNews.body_de : activeNews.body_en}
              </p>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* HERO FUNNEL */}
      <section className="relative flex items-center overflow-hidden py-20 md:py-28">
        <div className="absolute inset-0 z-0">
          <ParallaxImage src={HERO_IMG} alt="" direction="up" intensity={100} className="w-full h-full" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/85 to-[#050505]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/60" />
          <div
            className="absolute -right-20 top-1/4 h-[420px] w-[420px] rounded-full pointer-events-none"
            style={{
              background: "radial-gradient(circle, rgba(255,0,51,0.5) 0%, rgba(255,0,51,0) 70%)",
              filter: "blur(90px)",
              mixBlendMode: "screen",
              animation: "neonpulse 7s ease-in-out infinite",
            }}
          />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="fade-up">
            <Kicker>{t.home.kicker}</Kicker>
            <h1 className="font-heading text-4xl sm:text-6xl md:text-7xl lg:text-8xl uppercase tracking-tight leading-[0.9] text-white mt-5">
              {t.home.h1a} <span className="text-[#ff0033]">{t.home.h1b}</span>
            </h1>
            <p className="text-neutral-300 text-base md:text-lg max-w-2xl mt-7 leading-relaxed">
              {t.home.sub}
            </p>

            <div className="flex flex-wrap justify-start gap-10 md:gap-16 mt-10">
              {[
                [t.home.stat1, t.home.stat1l],
                [t.home.stat2, t.home.stat2l],
                [t.home.stat3, t.home.stat3l],
              ].map(([n, l]) => (
                <div key={l}>
                  <div className="font-heading text-4xl md:text-5xl text-white leading-none">{n}</div>
                  <div className="font-mono-lab text-[10px] tracking-[0.2em] text-neutral-500 uppercase mt-2">{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Call-to-Action Buttons */}
          <div className="mt-12 fade-up flex flex-wrap gap-4" style={{ animationDelay: "0.15s" }}>
            <Link to="/kurse" data-testid="hero-courses-link" className="inline-flex items-center gap-2 bg-[#ff0033] text-white font-heading uppercase tracking-wider px-7 py-4 hover:bg-[#cc0029] transition-colors">
              {t.home.learnCourses} <ArrowUpRight size={18} />
            </Link>
            <Link to="/einzeltraining" data-testid="hero-training-link" className="inline-flex items-center gap-2 border border-white/15 text-neutral-200 font-heading uppercase tracking-wider px-7 py-4 hover:border-[#ff0033] hover:text-white transition-colors">
              {lang === "de" ? "Trainerstunde buchen" : "Book coaching"} <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
